import { Gif } from '@remotion/gif';
import React, { useMemo } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import { RequireCachedAsset } from '../../caching/require-cached-asset';
import { useStablePreferredLocalUrl } from '../../utils/find-asset-by-id';
import { useAssetFromItem } from '../../utils/use-context';
import { useCroppableLayer } from '../croppable-layer';
import { ZoomWrapper } from '../zoom-wrapper';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from '../video/calculate-fade';
import { GifItem } from './gif-item-type';

export const GifLayer: React.FC<{
  item: GifItem;
  cropBackground: boolean;
  zIndex?: number;
}> = ({ item, cropBackground, zIndex }) => {
  if (item.type !== 'gif') {
    throw new Error('Item is not a gif');
  }

  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const asset = useAssetFromItem(item);

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const { innerStyle, outerStyle } = useCroppableLayer({
    item,
    rotation: item.rotation,
    opacity,
    borderRadius: item.borderRadius,
    cropBackground,
    zIndex,
  });

  // Animation 스타일 계산
  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  // outerStyle에 animation 적용
  const finalOuterStyle = useMemo(() => {
    return mergeAnimationStyle(outerStyle, animationStyle);
  }, [outerStyle, animationStyle]);

  const src = useStablePreferredLocalUrl(asset);

  return (
    <div style={finalOuterStyle}>
      <ZoomWrapper
        itemId={item.id}
        zoomKeyframes={cropBackground ? undefined : item.zoomKeyframes}
        frame={frame}
      >
        <RequireCachedAsset asset={asset}>
          <Gif
            style={innerStyle}
            src={src}
            playbackRate={item.playbackRate}
          />
        </RequireCachedAsset>
      </ZoomWrapper>
    </div>
  );
};
